import { useCallback, useEffect, useState } from 'react'

/**
 * Thème clair / sombre / système.
 *
 * Le thème était géré dans le Header, qui n'est monté que sur les écrans
 * authentifiés : la page de connexion restait donc toujours claire, et le
 * réglage n'était lu qu'après le premier rendu, d'où un flash blanc.
 *
 * Ici l'application de la classe est sortie de React (voir aussi le script
 * inline de index.html, qui fait le même travail avant le premier pixel) et
 * « système » est un choix à part entière, qui suit l'OS en direct.
 */

export const STORAGE_KEY = 'theme'
const MEDIA = '(prefers-color-scheme: dark)'

/** @returns {'light'|'dark'|'system'} */
export function getStoredTheme() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored
    // Migration de l'ancien réglage booléen `darkMode`.
    const legacy = localStorage.getItem('darkMode')
    if (legacy === 'true') return 'dark'
    if (legacy === 'false') return 'light'
  } catch {
    /* localStorage indisponible (navigation privée, cookies bloqués) */
  }
  return 'system'
}

export function resolveTheme(theme) {
  if (theme === 'system') {
    return typeof window !== 'undefined' && window.matchMedia(MEDIA).matches ? 'dark' : 'light'
  }
  return theme
}

export function applyTheme(theme) {
  const resolved = resolveTheme(theme)
  const root = document.documentElement
  root.classList.toggle('dark', resolved === 'dark')
  // Aligne la barre d'adresse mobile sur le fond réel de l'application.
  const meta = document.querySelector('meta[name="theme-color"]')
  if (meta) meta.setAttribute('content', resolved === 'dark' ? '#0a0c10' : '#ffffff')
  return resolved
}

export function useTheme() {
  const [theme, setThemeState] = useState(getStoredTheme)
  const [resolved, setResolved] = useState(() => resolveTheme(getStoredTheme()))

  useEffect(() => {
    setResolved(applyTheme(theme))
    try {
      localStorage.setItem(STORAGE_KEY, theme)
      localStorage.removeItem('darkMode')
    } catch {
      /* réglage non persisté : l'app reste utilisable */
    }
  }, [theme])

  // En mode « système », suivre l'OS sans recharger la page.
  useEffect(() => {
    if (theme !== 'system') return undefined
    const mq = window.matchMedia(MEDIA)
    const onChange = () => setResolved(applyTheme('system'))
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [theme])

  const setTheme = useCallback((next) => setThemeState(next), [])

  // Le bouton bascule entre clair et sombre en partant de ce qui est
  // réellement affiché : depuis « système », un clic donne le contraire de
  // ce que l'utilisateur a sous les yeux.
  const toggle = useCallback(() => {
    setThemeState(resolveTheme(getStoredTheme()) === 'dark' ? 'light' : 'dark')
  }, [])

  return { theme, resolvedTheme: resolved, isDark: resolved === 'dark', setTheme, toggle }
}
